import { API_BASE_URL } from '@/lib/apiBase'
import { authHeaders, handleUnauthorized } from '@/lib/authFetch'

/** Error thrown for any non-2xx response; carries the HTTP status and the API's `code`. */
export class ApiError extends Error {
  readonly status: number
  readonly code?: string
  /** The parsed JSON error body, when there was one (e.g. `{ problems: [] }` on a 422). */
  readonly body?: unknown

  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.body = body
  }
}

export interface RequestOptions extends RequestInit {
  /** Skip the 401 → sign-out redirect (credential-check routes like sign-in). */
  skipAuthRedirect?: boolean
}

/**
 * The one fetch wrapper every api client uses: base URL, bearer token, JSON in
 * and out, and a typed `ApiError` (message from the Nest error body when present).
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { skipAuthRedirect, headers, ...init } = options
  const res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers: authHeaders(headers) })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    let message = text || res.statusText
    let code: string | undefined
    let parsed: unknown
    try {
      const body = JSON.parse(text) as { message?: string | string[]; code?: string }
      parsed = body
      if (body.message) message = Array.isArray(body.message) ? body.message.join(', ') : body.message
      code = body.code
    } catch {
      // not JSON; keep the raw text
    }
    if (!skipAuthRedirect) handleUnauthorized(res.status)
    throw new ApiError(message || `Request failed (${res.status})`, res.status, code, parsed)
  }

  if (res.status === 204) return undefined as T
  const body = await res.text()
  return (body ? JSON.parse(body) : undefined) as T
}
